// =============================================================================
// Mobile Navigation - Bottom navigation bar for mobile devices
// =============================================================================

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { FolderKanban, Building2, Home, Menu, Search } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAccessControlStore } from '@/modules/core/hooks/useAccessControlStore';

const LAST_PROJECT_KEY = 'lapyno_last_project_id';

function getProjectsPath(): string {
  return '/projects';
}

interface MobileNavProps {
  onOpenSidebar: () => void;
}

export function MobileNav({ onOpenSidebar }: MobileNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { currentUserRole } = useAccessControlStore(currentOrganization?.id || null);
  const canAccessAdmin = currentUserRole === 'owner' || currentUserRole === 'admin';

  const navItems = [
    { icon: Home, path: '/', label: 'Accueil', color: '#10B981' },
    { icon: FolderKanban, path: getProjectsPath(), label: 'Projets', color: '#3B82F6', matchPrefix: '/projects' },
    { icon: Search, path: 'search', label: 'Rechercher', isAction: true, color: '#64748B' },
    ...(canAccessAdmin ? [{ icon: Building2, path: '/admin/settings', label: 'Admin', color: '#DC2626', matchPrefix: '/admin' }] : []),
  ];

  const isActive = (item: typeof navItems[0]) => {
    if (item.isAction) return false;
    if (item.matchPrefix) {
      return location.pathname.startsWith(item.matchPrefix);
    }
    return location.pathname === item.path;
  };

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.isAction && item.path === 'search') {
      // Trigger command palette
      const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
      document.dispatchEvent(event);
    } else {
      navigate(item.path);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const { icon: Icon, path, label, color, isAction } = item;
          const active = isActive(item);
          return (
            <button
              key={path}
              onClick={() => handleNavClick(item)}
              className={cn(
                "flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-lg transition-colors"
              )}
              style={{
                color: active ? color : 'hsl(var(--muted-foreground))'
              }}
            >
              <Icon className={cn("h-5 w-5", active && "stroke-[2.5]")} />
              <span className="text-[10px] font-medium">{label}</span>
            </button>
          );
        })}
        <button
          onClick={onOpenSidebar}
          className="flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
        >
          <Menu className="h-5 w-5" />
          <span className="text-[10px] font-medium">Plus</span>
        </button>
      </div>
    </nav>
  );
}
